import React, { useEffect, useState } from 'react';
import { useAthleteBuzz, BuzzKeyword, BuzzTheme } from '@/hooks/useAthleteBuzz';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  RefreshCw, 
  TrendingUp, 
  MessageSquare, 
  Newspaper, 
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface AthleteBuzzTabProps {
  athleteId: string;
  athleteName: string;
  teamName?: string;
  sport?: string;
}

type DateRange = '7d' | '30d' | '90d';

const getSentimentColor = (sentiment: 'positive' | 'neutral' | 'negative') => {
  switch (sentiment) {
    case 'positive':
      return 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20';
    case 'negative':
      return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const getSentimentIcon = (sentiment: 'positive' | 'neutral' | 'negative') => {
  switch (sentiment) {
    case 'positive':
      return <ThumbsUp className="h-3 w-3" />;
    case 'negative':
      return <ThumbsDown className="h-3 w-3" />;
    default:
      return <Minus className="h-3 w-3" />;
  }
};

const getKeywordSize = (count: number, maxCount: number) => {
  const ratio = count / maxCount;
  if (ratio > 0.7) return 'text-lg font-bold';
  if (ratio > 0.4) return 'text-base font-semibold';
  return 'text-sm font-medium';
};

export const AthleteBuzzTab: React.FC<AthleteBuzzTabProps> = ({
  athleteId,
  athleteName,
  teamName,
  sport,
}) => {
  const { buzzData, isLoading, error, fetchBuzz } = useAthleteBuzz();
  const [dateRange, setDateRange] = useState<DateRange>('7d');

  useEffect(() => {
    if (athleteId && athleteName) {
      fetchBuzz(athleteId, athleteName, teamName, sport, dateRange);
    }
  }, [athleteId, athleteName, teamName, sport, dateRange, fetchBuzz]);

  const handleRefresh = () => {
    fetchBuzz(athleteId, athleteName, teamName, sport, dateRange, true);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="relative">
          <Sparkles className="h-12 w-12 text-primary animate-pulse" />
          <div className="absolute inset-0 animate-ping opacity-20">
            <Sparkles className="h-12 w-12 text-primary" />
          </div>
        </div>
        <p className="mt-4 text-lg font-medium">Generating buzz...</p>
        <p className="text-sm text-muted-foreground mt-1">
          Analyzing news and social coverage for {athleteName}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <AlertCircle className="h-10 w-10 text-destructive mb-3" />
        <p className="text-destructive font-medium">Failed to load buzz data</p>
        <p className="text-sm text-muted-foreground mt-1">{error}</p>
        <Button variant="outline" size="sm" onClick={handleRefresh} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  if (!buzzData || (buzzData.keywords.length === 0 && buzzData.themes.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Sparkles className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-muted-foreground">No buzz data available</p>
        <p className="text-sm text-muted-foreground mt-1">
          No recent news coverage found for this athlete
        </p>
        <Button variant="outline" size="sm" onClick={handleRefresh} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Search Again
        </Button>
      </div>
    );
  }

  const maxKeywordCount = Math.max(...buzzData.keywords.map(k => k.count), 1);

  return (
    <div className="space-y-5">
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">
            {buzzData.totalArticles} articles analyzed
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border overflow-hidden">
            {(['7d', '30d', '90d'] as DateRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium transition-colors',
                  dateRange === range
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted'
                )}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="icon" onClick={handleRefresh} className="h-8 w-8">
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Keyword Cloud */}
      {buzzData.keywords.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <TrendingUp className="h-4 w-4" />
            Trending Keywords
          </div>
          <div className="flex flex-wrap gap-2">
            {buzzData.keywords.map((keyword, index) => (
              <Badge
                key={index}
                variant="outline"
                className={cn(
                  'cursor-default transition-all hover:scale-105',
                  getSentimentColor(keyword.sentiment),
                  getKeywordSize(keyword.count, maxKeywordCount)
                )}
                title={keyword.context}
              >
                {getSentimentIcon(keyword.sentiment)}
                <span className="ml-1">{keyword.keyword}</span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Themes */}
      {buzzData.themes.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <MessageSquare className="h-4 w-4" />
            What's Being Said
          </div>
          <div className="grid grid-cols-1 gap-2">
            {buzzData.themes.map((theme, index) => (
              <ThemeCard key={index} theme={theme} />
            ))}
          </div>
        </div>
      )}

      {/* Headlines */}
      {buzzData.headlines.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Newspaper className="h-4 w-4" />
            Recent Headlines
          </div>
          <div className="space-y-1.5">
            {buzzData.headlines.slice(0, 5).map((headline, index) => (
              <a
                key={index}
                href={headline.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors group"
              >
                <Badge
                  variant="outline"
                  className={cn('shrink-0 mt-0.5', getSentimentColor(headline.sentiment))}
                >
                  {getSentimentIcon(headline.sentiment)}
                </Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                    {headline.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{headline.source}</p>
                </div>
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Top Sources */}
      {buzzData.topSources.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Newspaper className="h-4 w-4" />
            Coverage Sources
          </div>
          <div className="flex flex-wrap gap-2">
            {buzzData.topSources.slice(0, 6).map((source, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {source.name}
                <span className="ml-1 opacity-60">({source.count})</span>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ThemeCard: React.FC<{ theme: BuzzTheme }> = ({ theme }) => {
  return (
    <div className={cn(
      'p-3 rounded-lg border',
      getSentimentColor(theme.sentiment)
    )}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-sm">{theme.theme}</span>
        <Badge variant="outline" className="text-xs">
          {theme.articles} {theme.articles === 1 ? 'article' : 'articles'}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">{theme.description}</p>
    </div>
  );
};

export default AthleteBuzzTab;
